/**
 * Reading a token, for the shell's own eyes only.
 *
 * **Nothing here is a security decision.** The signature is not checked, the audience is
 * not checked, and the tenant read here is not the tenant anything operates on. The
 * service verifies RS256 against the issuer's JWKS (`core/api/auth.py`) and resolves the
 * tenant from the verified claim itself (`core/api/deps.py::resolve_tenant`) — D-36 is
 * explicit that the module never asserts an org on the wire, because a trusted tenant
 * header would let anything that can reach the service read any tenant.
 *
 * Two things this is for, both of which need an answer before any request is made:
 *
 *   1. **Expiry.** A token past `exp` should send the user to the issuer rather than to a
 *      401, which is a round trip and a worse message.
 *   2. **Diagnostics.** D-12's tenant claim name is still open — the module reads the
 *      ordered fallback `org`, `org_id`, `tenant`, `tenant_id`, first *present* claim
 *      wins, and a token naming none of them is a hard 403 rather than a 401. When that
 *      403 arrives, "which spellings does your token actually carry" is the only useful
 *      question, and it can be answered here without the issuer's help.
 *
 * The authoritative tenant on every screen comes from `GET /v1/organizations/me`, not
 * from this file.
 */

/** The order `core/config.py::TENANT_CLAIM_FALLBACK` uses. Kept identical on purpose. */
export const TENANT_CLAIM_FALLBACK = ['org', 'org_id', 'tenant', 'tenant_id'] as const;

export interface TokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  iss?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  [claim: string]: unknown;
}

/** base64url → JSON. Returns null for anything that is not a readable JWT payload. */
export function decodeToken(token: string): TokenClaims | null {
  const parts = token.split('.');
  const payload = parts.length >= 2 ? parts[1] : undefined;
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '==='.slice((padded.length + 3) % 4));
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as TokenClaims;
  } catch {
    return null;
  }
}

/**
 * True when the token is absent, unreadable, or past `exp`.
 *
 * A token with no `exp` counts as expired. The service *requires* `exp` to be present
 * (`REQUIRED_CLAIMS` in core/api/auth.py), so a token without one is going to be refused
 * anyway, and treating it as valid here only delays finding that out.
 */
export function isExpired(token: string | null | undefined, skewSeconds = 30): boolean {
  if (!token) return true;
  const claims = decodeToken(token);
  if (!claims || typeof claims.exp !== 'number') return true;
  return Date.now() >= (claims.exp - skewSeconds) * 1000;
}

export interface TenantClaimReading {
  /** The spelling that won, or null when the token carries none of them. */
  name: string | null;
  value: string | null;
  /** Every recognised spelling present, so a token carrying two can be seen to. */
  present: ReadonlyArray<{ name: string; value: string }>;
}

/**
 * What the service will read, read the same way — first *present* spelling wins, no
 * fall-through. A token carrying `org` and `org_id` with different values is a token
 * nobody understands, and quietly preferring whichever one parses is how a request lands
 * in the wrong tenant. `present` exists so the shell can say that out loud.
 */
export function readTenantClaim(claims: TokenClaims | null): TenantClaimReading {
  if (!claims) return { name: null, value: null, present: [] };
  const present: Array<{ name: string; value: string }> = [];
  for (const name of TENANT_CLAIM_FALLBACK) {
    const raw = claims[name];
    if (raw === undefined || raw === null || raw === '') continue;
    present.push({ name, value: String(raw) });
  }
  const first = present[0];
  return {
    name: first?.name ?? null,
    value: first?.value ?? null,
    present,
  };
}

export function audienceList(claims: TokenClaims | null): string[] {
  const aud = claims?.aud;
  if (typeof aud === 'string') return [aud];
  if (Array.isArray(aud)) return aud.filter((a): a is string => typeof a === 'string');
  return [];
}
